<script setup lang="ts">
/**
 * Full-screen (or section) loading overlay for SCREENS — the branded Azzad
 * mark breathing inside a gold/green halo, over a soft frosted backdrop, with
 * an optional status line and an indeterminate progress rule.
 *
 * It is deliberately the SAME composition as app/spa-loading-template.html
 * (the pure-HTML screen shown before any JS runs): same halo, same
 * alternating gold/green ripples, same breathing mark, same sliding bar. A
 * user who signs in and then switches households should feel like they saw
 * one loader twice, not two different ones. All of it is CSS — see the
 * "Loading & motion system" block in assets/css/main.css — so nothing here
 * costs a network round-trip or main-thread work.
 *
 *   <AppScreenLoader :show="navigating" label="Loading…" />          <!-- fixed, full screen -->
 *   <AppScreenLoader :show="busy" :fullscreen="false" label="…" />   <!-- fills nearest relative parent -->
 *   <AppScreenLoader :show="busy" compact label="Saving…" />         <!-- spinner only, no mark -->
 *
 * `compact` swaps the mark for the ring spinner: use it for short in-place
 * waits (a save, a re-fetch) where the full brand moment would be overkill.
 */
import logoUrl from '~/assets/img/Azzad Asset Logo Symbol Square.png'

withDefaults(defineProps<{
  show?: boolean
  label?: string
  fullscreen?: boolean
  compact?: boolean
}>(), {
  show: true,
  label: '',
  fullscreen: true,
  compact: false
})
</script>

<template>
  <Transition name="azzad-fade">
    <div
      v-if="show"
      class="flex flex-col items-center justify-center gap-5 bg-gradient-to-tl from-[#E0E0D2]/80 to-[#C6C6BF]/80 backdrop-blur-[6px]"
      :class="fullscreen ? 'fixed inset-0 z-[60]' : 'absolute inset-0 z-10'"
      role="status"
      aria-live="polite"
    >
      <!-- Brand moment: halo + two alternating ripples + breathing mark.
           aria-hidden because the status text below already announces it. -->
      <div v-if="!compact" class="azzad-mark" aria-hidden="true">
        <span class="azzad-mark__ripple" />
        <span class="azzad-mark__ripple azzad-mark__ripple--alt" />
        <img :src="logoUrl" class="azzad-mark__logo" alt="">
      </div>

      <!-- Short in-place waits get the ring spinner instead. -->
      <span v-else class="azzad-spinner" aria-hidden="true" />

      <p
        v-if="label"
        class="text-sm font-nunito tracking-wide text-[#284E36] azzad-fade-in"
        style="--azzad-delay: 120ms"
      >
        {{ label }}
      </p>

      <div v-if="!compact" class="azzad-progress" aria-hidden="true" />

      <span class="sr-only">Loading</span>
    </div>
  </Transition>
</template>
